import React, {useEffect, useState} from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableRow, Typography, Paper, Box,
} from '@material-ui/core';
import { WithStyles } from '@material-ui/core/styles';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import { RouteComponentProps } from 'react-router-dom';
import styles from './styles';
import { defaultDateTimeFormat, formatDate } from '../../utils/date';
import { HubActivityLog } from '../../redux/types/hubs';
import { Order } from '../../utils/table';

const ActionMapping: Record<string, string> = {
  remove: 'Moved',
  activate: 'Activated',
  mask_as_sold: 'Sold',
};

const ColGroup = () => (
  <colgroup>
    <col style={{ width: '7%' }} />
    <col style={{ width: '12%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: '12%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: '16%' }} />
    <col style={{ width: '13%' }} />
    <col style={{ width: '12%' }} />
    <col style={{ width: '18%' }} />
  </colgroup>
);

const eventColors: Record<string, { bg: string; color: string }> = {
  activate: { bg: '#e6f9f0', color: '#1a9e6a' },
  mask_as_sold: { bg: '#ede7f6', color: '#7c3aed' },
  remove: { bg: '#fff8e1', color: '#f59e0b' },
};

type SortField = 'id' | 'hub_type' | 'lock_type' | 'property_id' | 'action' | 'created_on' | 'created_by' | 'created_by_email';

const sortFieldMap: Record<SortField, string> = {
  id: 'hub_activity_log.id',
  hub_type: 'hub_activity_log.hub_type',
  lock_type: 'hub_activity_log.lock_type',
  property_id: 'hub_activity_log.property_id',
  action: 'hub_activity_log.action',
  created_on: 'hub_activity_log.created_on',
  created_by: 'hub_activity_log.created_by',
  created_by_email: 'hub_activity_log.created_by_email',
};

type Props = {
  loadActivationLogsDetails: (serialNumber: string, orderBy: string, order: Order) => void;
  activationLogsDetails: HubActivityLog[];
} & WithStyles<typeof styles> & RouteComponentProps<{ serialNumber: string }>;

export const ActivationMapDetails = (props: Props) => {
  const {
    classes,
    loadActivationLogsDetails,
    activationLogsDetails,
    match,
  } = props;

  const [orderBy, setOrderBy] = useState('hub_activity_log.created_on');
  const [order, setOrder] = useState<Order>('desc');

  useEffect(() => {
    if (match.params.serialNumber) {
      loadActivationLogsDetails(match.params.serialNumber, orderBy, order);
    }
  }, [orderBy, order, loadActivationLogsDetails, match.params.serialNumber]);

  const handleSort = (field: SortField) => {
    const backendField = sortFieldMap[field];
    if (orderBy === backendField) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(backendField);
      setOrder('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    const backendField = sortFieldMap[field];
    if (orderBy !== backendField) return null;
    return order === 'asc'
      ? <ArrowUpward className={classes.sortIcon} />
      : <ArrowDownward className={classes.sortIcon} />;
  };

  return (
    <Paper className={classes.root}>
      <Box className={classes.titleContainer}>
        <Typography component="span" className={classes.title}>Activation History</Typography>
      </Box>

      <Table className={classes.table}>
        <ColGroup />
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell
              onClick={() => handleSort('id')}
              className={classes.sortableCell}
            >
              <Box className={classes.sortCell}>
                ID
                <SortIcon field="id" />
              </Box>
            </TableCell>
            <TableCell
              onClick={() => handleSort('hub_type')}
              className={classes.sortableCell}
            >
              <Box className={classes.sortCell}>
                Hub Type
                <SortIcon field="lock_type" />
              </Box>
            </TableCell>
            <TableCell
              onClick={() => handleSort('lock_type')}
              className={classes.sortableCell}
            >
              <Box className={classes.sortCell}>
                Lock Type
                <SortIcon field="hub_type" />
              </Box>
            </TableCell>
            <TableCell className={classes.sortableCell}>
              <Box className={classes.sortCell}>
                Serial number
              </Box>
            </TableCell>
            <TableCell
              onClick={() => handleSort('property_id')}
              className={classes.sortableCell}
            >
              <Box className={classes.sortCell}>
                Property ID
                <SortIcon field="property_id" />
              </Box>
            </TableCell>
            <TableCell
              onClick={() => handleSort('action')}
              className={classes.sortableCell}
            >
              <Box className={classes.sortCell}>
                Action
                <SortIcon field="action" />
              </Box>
            </TableCell>
            <TableCell
              onClick={() => handleSort('created_on')}
              className={classes.sortableCell}
            >
              <Box className={classes.sortCell}>
                Timestamp
                <SortIcon field="created_on" />
              </Box>
            </TableCell>
            <TableCell
              onClick={() => handleSort('created_by')}
              className={classes.sortableCell}
            >
              <Box className={classes.sortCell}>
                User
                <SortIcon field="created_by" />
              </Box>
            </TableCell>
            <TableCell
              onClick={() => handleSort('created_by_email')}
              className={classes.sortableCell}
            >
              <Box className={classes.sortCell}>
                User email
                <SortIcon field="created_by_email" />
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
      </Table>

      <Box className={classes.tableBody}>
        <Table className={classes.table}>
          <ColGroup />
          <TableBody>
            {activationLogsDetails.map((log) => (
              <TableRow key={log.id} className={classes.tableRow}>
                <TableCell align="center">{log.id}</TableCell>
                <TableCell align="center">{log.hub_type}</TableCell>
                <TableCell align="center">{log.lock_type ?? '—'}</TableCell>
                <TableCell align="center">{log.serial_number}</TableCell>
                <TableCell align="center">{log.property_id}</TableCell>
                <TableCell align="center">
                  <span
                    className={classes.eventBadge}
                    style={{
                      background: eventColors[log?.action]?.bg,
                      color: eventColors[log?.action]?.color,
                    }}
                  >
                    {ActionMapping[log?.action]}
                  </span>
                </TableCell>
                <TableCell align="center">{formatDate(log.created_on, defaultDateTimeFormat)}</TableCell>
                <TableCell align="center">{log.created_by}</TableCell>
                <TableCell align="center">{log.created_by_email}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};
